import { useCallback, useEffect, useMemo, useState } from "react";

import { clusterColor, StackedEmissionChart, WindEmissionScatter, type StackedData, type WindPoint } from "./analytics-charts.js";
import type { Cluster } from "./ClusterCard.js";
import { formatMass } from "./EmissionBarChart.js";
import { Icon } from "./Icon.js";
import { ApiError } from "../api/client.js";
import type { TelemetrySnapshot } from "../api/telemetry.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda ANALYTICS, portata da `AnalyticsView.qml`.
 *
 * E' la vista di SITO: tre numeri in cima, le emissioni nel tempo impilate per
 * impianto, la correlazione col vento, e la tabella dello stato degli
 * apparati. La pagina EMISSIONI risponde a "quanto ha emesso"; questa risponde
 * a "come sta andando l'impianto, e di chi e' quel numero".
 *
 * I filtri in alto valgono per tutto quello che sta sotto — periodo e quali
 * impianti includere — e il colore di ogni impianto e' lo stesso nelle
 * pastiglie, nel grafico impilato e nello scatter: cambiarlo fra un riquadro e
 * l'altro trasformerebbe la lettura in un esercizio di memoria.
 */

type Preset = "24h" | "7d" | "30d" | "1y";

const PRESETS: { key: Preset; label: string; period: string }[] = [
  { key: "24h", label: "emissions.last24h", period: "day" },
  { key: "7d", label: "emissions.last7d", period: "week" },
  { key: "30d", label: "emissions.last30d", period: "month" },
  { key: "1y", label: "emissions.lastYear", period: "year" },
];

/** Sopra questa concentrazione un impianto conta come perdita attiva. Stessa
 *  soglia del `.qml` e della nuvola di gas sulla pianta. */
const ACTIVE_LEAK_PPM = 150;

interface ClusterEmission {
  clusterId: string;
  clusterName: string;
  totalMassKg: number;
  uncertaintyPct: number;
  qualityClass: string;
  quality: { overallCoveragePct: number };
}

interface Summary {
  grandTotal: { totalMassKg: number; uncertaintyPct: number };
  clusters: ClusterEmission[];
}

export function AnalyticsTab({
  telemetry = {},
  onOpenCluster,
}: {
  telemetry?: TelemetrySnapshot;
  onOpenCluster?: ((c: Cluster) => void) | undefined;
}) {
  const { t, reportLabels } = useI18n();
  const { api, can } = useSession();

  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [preset, setPreset] = useState<Preset>("30d");
  /** `null` = tutti. Un insieme vuoto vorrebbe dire "nessuno", che non e' la
   *  stessa cosa e lascerebbe i grafici vuoti senza spiegazione. */
  const [enabled, setEnabled] = useState<string[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [stacked, setStacked] = useState<StackedData | null>(null);
  const [wind, setWind] = useState<WindPoint[]>([]);
  const [pdfBusy, setPdfBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void api
      .call<Cluster[]>("clusters.list")
      .then(setClusters)
      .catch(() => {
        /* la tabella lo dice da sola */
      });
  }, [api]);

  const period = PRESETS.find((p) => p.key === preset)?.period ?? "month";

  const load = useCallback(async () => {
    const date = new Date().toISOString();
    try {
      const [s, st, w] = await Promise.all([
        api.call<Summary>("emissions.summary", { period, date }),
        api.call<StackedData>("emissions.stacked", { period, date }),
        api.call<WindPoint[]>("emissions.wind_emission", {
          period,
          date,
          ...(enabled ? { clusterIds: enabled.join(",") } : {}),
        }),
      ]);
      setSummary(s);
      // Il filtro sul grafico impilato si applica QUI e non sul server: la
      // rotta manda l'asse dei tempi comune a tutti, e chiederne uno per
      // sottoinsieme darebbe barre non allineate fra un filtro e l'altro.
      setStacked(
        enabled
          ? { periods: st.periods ?? [], clusters: (st.clusters ?? []).filter((c) => enabled.includes(c.clusterId)) }
          : st,
      );
      setWind(w ?? []);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api, period, enabled]);

  useEffect(() => void load(), [load]);

  const activeLeaks = useMemo(
    () => Object.values(telemetry).filter((s) => (s.ppm ?? 0) > ACTIVE_LEAK_PPM).length,
    [telemetry],
  );

  const clusterNames = useMemo(
    () => Object.fromEntries(clusters.map((c) => [c.id, c.name])),
    [clusters],
  );

  const toggle = (id: string) => {
    // Il primo clic su una pastiglia isola il resto: e' la scorciatoia per
    // "tutti tranne questo", che e' il gesto piu' frequente.
    if (!enabled) {
      setEnabled(clusters.map((c) => c.id).filter((x) => x !== id));
      return;
    }
    const next = enabled.includes(id) ? enabled.filter((x) => x !== id) : [...enabled, id];
    setEnabled(next.length === clusters.length ? null : next);
  };

  const downloadPdf = async (route: "reports.site_pdf" | "reports.cluster_pdf", busyId: string, body: object) => {
    if (pdfBusy) return;
    setPdfBusy(busyId);
    try {
      const r = await api.call<{ url: string; filename: string }>(route, {
        period,
        date: new Date().toISOString(),
        labels: reportLabels(),
        ...body,
      });
      const a = document.createElement("a");
      a.href = r.url;
      a.download = r.filename;
      a.click();
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPdfBusy("");
    }
  };

  const rows = clusters.map((c) => {
    const tel = telemetry[c.id];
    const sc = summary?.clusters.find((x) => x.clusterId === c.id);
    const online = tel?.sensorOnline === true;
    return {
      cluster: c,
      online,
      totalMassKg: sc ? Number(sc.totalMassKg) : 0,
      uncertaintyPct: sc ? Number(sc.uncertaintyPct) : 0,
      qualityClass: sc?.qualityClass ?? "Poor",
      // Il picco si mostra solo se supera la soglia di avviso: sotto non e'
      // un picco, e' rumore.
      lastPeakPpm:
        tel?.ppm !== undefined && tel.ppm > (c.thresholdL1 ?? ACTIVE_LEAK_PPM) ? tel.ppm : null,
      coveragePct: sc?.quality ? Number(sc.quality.overallCoveragePct) : 0,
      lastReadingAt: online ? new Date().toISOString() : null,
      windEnabled: Boolean(c.windEnabled),
    };
  });

  return (
    <div className="settings__pane analytics">
      {error && <p className="alert">{error}</p>}

      <h3 className="admin__title">FILTERS</h3>
      <div className="afilters">
        <div className="presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`preset${preset === p.key ? " preset--on" : ""}`}
              onClick={() => setPreset(p.key)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>

        {can("exportReports") && (
          <button
            className="emissions__export"
            disabled={pdfBusy !== ""}
            onClick={() =>
              void downloadPdf("reports.site_pdf", "site", {
                clusterIds: (enabled ?? clusters.map((c) => c.id)).join(","),
              })
            }
          >
            <Icon name="file-text" size={14} />
            {pdfBusy === "site" ? t("generatingPdf") : t("exportPdf")}
          </button>
        )}

        <button
          className={`chip2${enabled === null ? " chip2--on" : ""}`}
          onClick={() => setEnabled(null)}
        >
          {t("analytics.allClusters")}
        </button>
        {clusters.map((c, i) => {
          const on = enabled === null || enabled.includes(c.id);
          return (
            <button
              key={c.id}
              className="chip2"
              style={{
                borderColor: on ? clusterColor(i) : "rgba(255,255,255,0.2)",
                color: on ? clusterColor(i) : "rgba(255,255,255,0.72)",
                background: on ? `${clusterColor(i).replace("hsl", "hsla").replace(")", ", 0.16)")}` : "transparent",
              }}
              onClick={() => toggle(c.id)}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      <h3 className="admin__title">{t("hdr.emissionMetrics")}</h3>
      <div className="ametrics">
        <div>
          <div className="ametric__label">{t("analytics.totalSiteEmissions").toUpperCase()}</div>
          <div className="ametric__value">
            {summary ? formatMass(summary.grandTotal?.totalMassKg ?? 0) : "—"}
          </div>
        </div>
        <div>
          <div className="ametric__label">{t("analytics.activeLeaks").toUpperCase()}</div>
          <div className={`ametric__value${activeLeaks > 0 ? " ametric__value--crit" : ""}`}>
            {activeLeaks}
          </div>
          <div className="ametric__sub">ppm &gt; {ACTIVE_LEAK_PPM}</div>
        </div>
        <div>
          <div className="ametric__label">{t("analytics.siteUncertainty").toUpperCase()}</div>
          <div className="ametric__value ametric__value--warn">
            {summary ? `${Number(summary.grandTotal?.uncertaintyPct ?? 0).toFixed(1)}%` : "—"}
          </div>
        </div>
      </div>

      <h3 className="admin__title">{t("hdr.emissionsOverTime")}</h3>
      <StackedEmissionChart data={stacked} />

      <h3 className="admin__title">{t("hdr.windVsEmission")}</h3>
      <WindEmissionScatter points={wind} clusterNames={clusterNames} />

      <h3 className="admin__title">{t("hdr.assetStatus")}</h3>
      <div className="atable">
        <table className="table">
          <thead>
            <tr>
              <th>{t("settings.name")}</th>
              <th>STATUS</th>
              <th className="ta-right">{t("emissions.totalEmissions").toUpperCase()}</th>
              <th className="ta-right">{t("emissions.uncertainty").toUpperCase()}</th>
              <th className="ta-center">QUALITY</th>
              <th className="ta-right">{t("analytics.lastPeak").toUpperCase()}</th>
              <th className="ta-right">{t("analytics.dataCoverage").toUpperCase()}</th>
              <th>{t("analytics.lastReading").toUpperCase()}</th>
              <th className="ta-center">WIND</th>
              {can("exportReports") && <th className="ta-center">PDF</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cluster.id} onClick={() => onOpenCluster?.(r.cluster)}>
                <td className="emissions__name">{r.cluster.name}</td>
                <td>
                  <span className={`sitemap__st sitemap__st--${r.online ? "normal" : "offline"}`}>
                    <i className="dot" />
                    {r.online ? t("focus.online") : t("focus.offline")}
                  </span>
                </td>
                <td className="ta-right mono">{formatMass(r.totalMassKg)}</td>
                <td className="ta-right mono">{r.uncertaintyPct.toFixed(1)}%</td>
                <td className="ta-center">
                  <span className={`qual qual--${r.qualityClass.toLowerCase()}`}>
                    {r.qualityClass}
                  </span>
                </td>
                {/* Nessun picco non e' zero: e' "nessuna lettura sopra soglia". */}
                <td className="ta-right mono">
                  {r.lastPeakPpm === null ? "-" : `${Math.round(r.lastPeakPpm)} ppm`}
                </td>
                <td className="ta-right mono">{r.coveragePct.toFixed(1)}%</td>
                <td className="mono">{formatShortTime(r.lastReadingAt)}</td>
                <td className="ta-center">
                  {r.windEnabled ? <Icon name="wind" size={16} /> : <span className="muted">-</span>}
                </td>
                {can("exportReports") && (
                  <td className="ta-center">
                    <button
                      className="iconbtn"
                      disabled={pdfBusy !== ""}
                      title={t("exportPdf")}
                      onClick={(e) => {
                        e.stopPropagation();
                        void downloadPdf("reports.cluster_pdf", r.cluster.id, {
                          clusterId: r.cluster.id,
                        });
                      }}
                    >
                      <Icon name="download" size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="muted">
                  {t("emissions.noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatShortTime(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
